import React, {useState} from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField, Typography } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { adminAddChallenge } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { challengeCategories } from '../Academy/skillTreeData';

interface PropsAdminChallenges {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const selectSx = (colors: any) => ({
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.9rem',
    color: colors.text.primary,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent.cyan },
    '& .MuiSvgIcon-root': { color: colors.text.secondary },
});

const dockerImages = ["all:latest", "daml:latest", "sui:latest", "solana:latest", "bitcoin:latest", "cairo:latest"];

const AdminAddChallenge = (props: PropsAdminChallenges) => {
    const colors = useColors();
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [name, setName] = useState("");
    const [app, setApp] = useState("");
    const [active, setActive] = useState(false);
    const [difficulty, setDifficulty] = useState(0);
    const [points, setPoints] = useState(0);
    const [type, setType] = useState(0);
    const [category, setCategory] = useState("introduction");
    const [dockerImage, setDockerImage] = useState("all:latest");

    const token = props.token;

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminAddChallenge(token, {
            name, app, active, difficulty, points, type, category, docker_image: dockerImage.trim() || "all:latest"
        });
        setProcessing(false);
        setMessage(response.msg);
    };

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: 'Challenges', to: '/admin/challenges' },
                    { label: 'Add' },
                ]}
                mb="32px"
            />

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '600px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                            />
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                label="App"
                                value={app}
                                onChange={(e) => setApp(e.target.value)}
                            />
                            <FormControl fullWidth>
                                <InputLabel sx={{
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.82rem',
                                    color: colors.text.secondary,
                                    '&.Mui-focused': { color: colors.accent.cyan },
                                }}>Docker Image</InputLabel>
                                <Select
                                    value={dockerImage}
                                    label="Docker Image"
                                    onChange={(e) => setDockerImage(e.target.value)}
                                    sx={selectSx(colors)}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                >
                                    {dockerImages.map(image => (
                                        <MenuItem key={image} value={image} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>
                                            {image}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Difficulty"
                                    type="number"
                                    value={difficulty}
                                    onChange={(e) => setDifficulty(Number(e.target.value))}
                                />
                                <TextField
                                    sx={{ ...fieldSx(colors), flex: 1 }}
                                    required
                                    label="Points"
                                    type="number"
                                    value={points}
                                    onChange={(e) => setPoints(Number(e.target.value))}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: '16px' }}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        color: colors.text.secondary,
                                        '&.Mui-focused': { color: colors.accent.cyan },
                                    }}>Type</InputLabel>
                                    <Select
                                        value={type}
                                        label="Type"
                                        onChange={(e) => setType(Number(e.target.value))}
                                        sx={selectSx(colors)}
                                        MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                    >
                                        <MenuItem value={0} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>Academy</MenuItem>
                                        <MenuItem value={1} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>Mission</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        color: colors.text.secondary,
                                        '&.Mui-focused': { color: colors.accent.cyan },
                                    }}>Category</InputLabel>
                                    <Select
                                        value={category}
                                        label="Category"
                                        onChange={(e) => setCategory(e.target.value)}
                                        sx={selectSx(colors)}
                                        MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                    >
                                        {challengeCategories.map(c => (
                                            <MenuItem key={c} value={c} sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.85rem' }}>
                                                {c}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.accent.cyan },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.accent.cyanDim },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{
                                        fontFamily: '"Share Tech Mono", monospace',
                                        fontSize: '0.82rem',
                                        letterSpacing: '0.08em',
                                        color: colors.text.secondary,
                                    }}>
                                        Active
                                    </Typography>
                                }
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <CheckCircleOutlineIcon />}
                                sx={{
                                    marginTop: '8px',
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: colors.accent.cyanGlow,
                                        borderColor: colors.accent.cyan,
                                        boxShadow: `0 0 20px ${colors.accent.cyan}33`,
                                    },
                                    '&:disabled': {
                                        borderColor: colors.border.default,
                                        color: colors.text.muted,
                                    },
                                }}
                            >
                                {processing ? 'Adding...' : 'Add Challenge'}
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                    letterSpacing: '0.06em',
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>
        </PageContainer>
    );
};

export default AdminAddChallenge;
